import { useState, useMemo } from "react"

import lodash from "lodash"
import deepdash from "deepdash"
import { lang } from "config"
import Label from "components/forms/label"
import Input from "components/forms/input"
import InputAmount from "components/forms/input-amount"
import Description from "components/forms/description"
import Checkbox from "components/forms/checkbox"
import Select from "components/forms/select"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import useSatuanRepositories from "repositories/satuan"

export default function Form({ data, errors, onSubmit }) {
    const _ = deepdash(lodash)

    const id = data?.id

    const [name, setName] = useState(data?.name ?? "")
    const [description, setDescription] = useState(data?.description ?? "")
    const [isBase, setIsBase] = useState(Boolean(data?.is_base))
    const [baseSatuanId, setBaseSatuanId] = useState(data?.base_satuan_id ?? null)
    const [qtyPerUnit, setQtyPerUnit] = useState(data?.qty_per_unit != null ? Number(data.qty_per_unit) : 1)

    // Daftar satuan dasar (untuk pacuan). Hanya yang is_base = true.
    const { data: baseSatuans } = useSatuanRepositories({ is_base: 1, paginate: false })

    // Buang dirinya sendiri agar tidak bisa memilih diri sendiri sebagai pacuan.
    const baseOptions = useMemo(() => {
        const list = Array.isArray(baseSatuans) ? baseSatuans : (baseSatuans?.data ?? [])
        return list.filter((row) => row.id !== id)
    }, [baseSatuans, id])

    const handleSubmit = (e) => {
        e.preventDefault()

        if (nProgress.isStarted()) {
            toast.error("Aksi sebelumnya sedang berjalan")
            return
        }

        onSubmit({
            id,
            name,
            description,
            is_base: isBase,
            base_satuan_id: isBase ? null : baseSatuanId,
            qty_per_unit: isBase ? 1 : qtyPerUnit,
        })
    }

    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
            <div className="mt-10 sm:mt-0">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    <div className="md:col-span-1">
                        <div className="px-4 sm:px-0">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{lang.main_form}</h3>
                            <p className="mt-1 text-sm text-gray-600">
                                Satuan dasar (mis. <b>pcs</b>) menjadi pacuan untuk satuan lain. Contoh: 1 lusin = 12 pcs, 1 box = 4 pcs.
                            </p>
                        </div>
                    </div>
                    <div className="mt-5 md:col-span-2 md:mt-0">
                        <div className="border sm:rounded-xl">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="name" value={lang.name} />
                                        <Input
                                            placeholder="pcs / lusin / box"
                                            onChange={(e) => setName(e.target.value)}
                                            value={name}
                                            id="name"
                                            error={errors?.name}
                                        />
                                        <Description error={errors?.name} />
                                    </div>

                                    <div>
                                        <Label htmlFor="description" value="Keterangan" />
                                        <Input
                                            placeholder="Opsional"
                                            onChange={(e) => setDescription(e.target.value)}
                                            value={description ?? ""}
                                            id="description"
                                            error={errors?.description}
                                        />
                                        <Description error={errors?.description} />
                                    </div>

                                    <div className="col-span-2 flex items-center space-x-2">
                                        <Checkbox
                                            id="is_base"
                                            checked={isBase}
                                            onChange={(e) => {
                                                const checked = e.target.checked
                                                setIsBase(checked)
                                                if (checked) {
                                                    setBaseSatuanId(null)
                                                    setQtyPerUnit(1)
                                                }
                                            }}
                                        />
                                        <Label htmlFor="is_base" value="Jadikan satuan dasar (pacuan)" />
                                    </div>

                                    {!isBase && (
                                        <>
                                            <div>
                                                <Label htmlFor="base_satuan_id" value="Pacuan (satuan dasar)" />
                                                <Select
                                                    placeholder="Pilih satuan dasar"
                                                    selection={baseOptions}
                                                    keyValue="id"
                                                    display="name"
                                                    value={baseSatuanId}
                                                    onChange={(value) => setBaseSatuanId(value)}
                                                    error={errors?.base_satuan_id}
                                                />
                                                <Description error={errors?.base_satuan_id} />
                                            </div>

                                            <div>
                                                <Label htmlFor="qty_per_unit" value="Qty per Satuan (terhadap pacuan)" />
                                                <InputAmount
                                                    placeholder="mis. 12"
                                                    onChange={(value) => setQtyPerUnit(value)}
                                                    value={qtyPerUnit}
                                                    id="qty_per_unit"
                                                    decimalScale={4}
                                                    error={errors?.qty_per_unit}
                                                />
                                                <Description error={errors?.qty_per_unit} />
                                                <p className="mt-1 text-xs text-neutral-500">
                                                    Berapa banyak satuan dasar dalam 1 {name || "satuan"} ini.
                                                </p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 text-xs text-right">
                <button type="submit" className="items-center px-6 py-3 text-white transition bg-brand-800 rounded-xl active:hover:scale-90">
                    <span>{`${_.isEmpty(data) ? lang.create : lang.update}`}</span>
                </button>
            </div>
        </form>
    )
}
